/** Master's minimum length of an account password (`minPasswordLength`). */
export const NEW_PASSWORD_MIN_LENGTH = 8;

/**
 * What keeps a new password from being taken: shorter than the minimum, the
 * same as the current password, or a repetition that does not match.
 */
export type NewPasswordProblem = 'too_short' | 'unchanged' | 'mismatch';

export interface NewPasswordRules {
  /** Defaults to {@link NEW_PASSWORD_MIN_LENGTH}. */
  minLength?: number;
  /** When given, a new password equal to it is refused. */
  currentPassword?: string;
}

/**
 * The client-side twin of master's check (`checkNewPassword`), so a form can
 * say what is wrong before it sends anything. The first problem wins, in the
 * order the fields are filled: length, then „not the current one", then the
 * repetition. `null` means the pair can be sent.
 *
 * Counts code points, not UTF-16 units — the characters master counts too,
 * so client and server agree on "too short".
 */
export function newPasswordProblem(
  newPassword: string,
  repeat: string,
  rules: NewPasswordRules = {}
): NewPasswordProblem | null {
  const minLength = rules.minLength ?? NEW_PASSWORD_MIN_LENGTH;
  if ([...newPassword].length < minLength) return 'too_short';
  if (rules.currentPassword && newPassword === rules.currentPassword) return 'unchanged';
  if (newPassword !== repeat) return 'mismatch';
  return null;
}
